"use client";

import { useState, useEffect } from "react";
import { useParams, useRouter } from "next/navigation";
import { ChevronLeft } from "lucide-react";
import { getItem } from "@/lib/api/products";
import { Item } from "@/lib/api/products";
import { getUserProfile } from "@/lib/profile/api";
import { UserProfile } from "@/data/profile/types";
import { getItemList } from "@/lib/api/products";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/lib/hooks/useAuth";
import { getOrCreateChat } from "@/lib/chat/api";
import toast from "react-hot-toast";
import { ProductImageCarousel } from "@/components/product/ProductImageCarousel";
import { ProductCoreInfo } from "@/components/product/ProductCoreInfo";
import { SellerCard } from "@/components/product/SellerCard";
import { ProductSpecsCard } from "@/components/product/ProductSpecsCard";
import { ProductDescription } from "@/components/product/ProductDescription";
import { ProductCTA } from "@/components/product/ProductCTA";
import { RecommendedItems, RecommendedItemsSkeleton } from "@/components/RecommendedItems";
import { getRecommendedItems } from "@/lib/utils/getRecommendedItems";
import { ProductSchema } from "@/components/product/ProductSchema";
import EditProductModal from "@/components/product/EditProductModal";

export default function ProductDetailPage() {
  const params = useParams();
  const router = useRouter();
  const { user } = useAuth();
  const itemId = params?.id as string;

  const [item, setItem] = useState<Item | null>(null);
  const [sellerProfile, setSellerProfile] = useState<UserProfile | null>(null);
  const [sellerItems, setSellerItems] = useState<Item[]>([]);
  const [recommendedItems, setRecommendedItems] = useState<Item[]>([]);
  const [isLiked, setIsLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (itemId) {
      loadItemDetail();
    }
  }, [itemId]);

  const loadItemDetail = async () => {
    try {
      setLoading(true);
      const result = await getItem(itemId);
      if (result.success && result.item) {
        setItem(result.item);
        await loadSellerProfile(result.item.sellerUid);
        await loadSellerItems(result.item.sellerUid, result.item.id);
        await loadRecommendedItems(result.item);
        
        // 조회한 카테고리 저장 (로컬 스토리지)
        const category = (result.item as any).category;
        if (category) {
          try {
            const viewed = localStorage.getItem("viewedCategories");
            const categories = viewed ? JSON.parse(viewed) : [];
            if (!categories.includes(category)) {
              categories.push(category);
              localStorage.setItem("viewedCategories", JSON.stringify(categories.slice(-10))); // 최근 10개만
            }
          } catch (e) {
            // 무시
          }
        }
      }
    } catch (error) {
      console.error("상품 상세 로드 실패:", error);
      toast.error("상품 정보를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const loadSellerProfile = async (sellerUid: string) => {
    try {
      const profile = await getUserProfile(sellerUid);
      if (profile.success && profile.profile) {
        setSellerProfile(profile.profile);
      }
    } catch (error) {
      console.error("판매자 프로필 로드 실패:", error);
    }
  };

  const loadSellerItems = async (sellerUid: string, excludeId: string) => {
    try {
      const result = await getItemList({
        limit: 10,
        filters: {
          status: "active",
        },
      });
      if (result.success && result.items) {
        const filtered = result.items
          .filter(i => i.sellerUid === sellerUid && i.id !== excludeId)
          .slice(0, 3);
        setSellerItems(filtered);
      }
    } catch (error) {
      console.error("판매자 상품 로드 실패:", error);
    }
  };

  const loadRecommendedItems = async (currentItem: Item) => {
    setRecommendedLoading(true);
    try {
      // Firebase에서 직접 추천 상품 가져오기
      const recommended = await getRecommendedItems(currentItem, 6, false);
      setRecommendedItems(recommended);
    } catch (error) {
      console.error("추천 상품 로드 실패:", error);
      // Fallback: 기존 방식 사용
      try {
        const result = await getItemList({
          limit: 50,
          filters: {
            status: "active",
            category: (currentItem as any).category,
          },
        });
        if (result.success && result.items) {
          const filtered = result.items
            .filter((i) => i.id !== currentItem.id)
            .slice(0, 6);
          setRecommendedItems(filtered);
        }
      } catch (fallbackError) {
        console.error("Fallback 추천 상품 로드 실패:", fallbackError);
      }
    } finally {
      setRecommendedLoading(false);
    }
  };

  const handleChat = async () => {
    if (!user) {
      router.push("/auth/login?next=/product/" + itemId);
      return;
    }
    if (!item) return;

    try {
      const chatResult = await getOrCreateChat(item.id, item.sellerUid);
      if (chatResult.success && chatResult.chatId) {
        router.push(`/chat?chatId=${chatResult.chatId}`);
      } else {
        toast.error("채팅을 시작할 수 없습니다.");
      }
    } catch (error) {
      console.error("채팅 시작 실패:", error);
      toast.error("채팅을 시작할 수 없습니다.");
    }
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = () => {
    if (confirm("정말 삭제하시겠습니까?")) {
      // 삭제 로직 구현
      toast.success("삭제되었습니다.");
      router.push("/list");
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    // TODO: 찜하기 API 호출
    toast.success(isLiked ? "찜하기 해제되었습니다." : "찜하기에 추가되었습니다.");
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("ko-KR").format(price);
  };

  const handleShare = () => {
    if (!item) return;
    const itemTitle = (item as any).title || `${(item as any).brand || ""} ${(item as any).model || ""}`.trim() || "악기";
    if (navigator.share) {
      navigator.share({
        title: itemTitle,
        text: `${itemTitle} - ${formatPrice(item.price)}원`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("링크가 복사되었습니다.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8FC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF]"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-[#F9F8FC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            상품을 찾을 수 없습니다
          </h2>
          <Button onClick={() => router.push("/list")}>목록으로 돌아가기</Button>
        </div>
      </div>
    );
  }

  const title = (item as any).title || `${(item as any).brand || ""} ${(item as any).model || ""}`.trim() || "악기";
  const images = item.images || [];
  const isOwner = user?.uid === item.sellerUid;
  
  // 거래 상태 확인
  const isTrading = item.status === "reserved" || item.status === "escrow_completed" || item.status === "shipping";
  const isSold = item.status === "sold";
  const isDisabled = isTrading || isSold;

  return (
    <>
      {/* 구조화된 데이터 (JSON-LD) */}
      <ProductSchema item={item} sellerProfile={sellerProfile} />
      
      <div className="min-h-screen bg-[#F9F8FC] pb-24">
        {/* 뒤로가기 버튼 */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-screen-sm mx-auto px-4 py-3">
          <button
            onClick={() => router.back()}
            className="flex items-center text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span className="text-sm">뒤로</span>
          </button>
        </div>
      </div>

      <div className="max-w-screen-sm mx-auto px-4 py-6 space-y-6">
        {/* [1] 상품 이미지 캐러셀 */}
        <ProductImageCarousel images={images} title={title} item={item} />

        {/* [2] 상품 핵심 정보 */}
        <ProductCoreInfo
          category={(item as any).category}
          subcategory={(item as any).subcategory}
          brand={(item as any).brand}
          model={(item as any).model}
          title={title}
          price={item.price}
          isLiked={isLiked}
          onLike={handleLike}
          onShare={handleShare}
        />

        {/* [3] 판매자 정보 섹션 */}
        {sellerProfile && (
          <SellerCard
            sellerProfile={sellerProfile}
            sellerUid={item.sellerUid}
            reviewCount={0} // TODO: 실제 후기 수 가져오기
            isOnline={false} // TODO: 온라인 여부 확인
          />
        )}

        {/* [4] 상품 상세 정보 */}
        <ProductSpecsCard
          condition={(item as any).condition}
          year={(item as any).year}
          shippingTypes={(item as any).shippingTypes || []}
          region={item.region}
          components={(item as any).components}
          defects={(item as any).defects}
        />

        {/* [5] 설명 섹션 */}
        {(item as any).description && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">설명</h2>
            <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
              {(item as any).description}
            </div>
          </div>
        )}

        {/* [6] 유사 매물 추천 */}
        {recommendedLoading ? (
          <RecommendedItemsSkeleton />
        ) : recommendedItems.length > 0 ? (
          <RecommendedItems
            items={recommendedItems}
            title="유사한 매물 추천"
            showMoreLink={true}
          />
        ) : null}
      </div>

      {/* [7] 하단 고정 CTA */}
      <ProductCTA
        isLiked={isLiked}
        isDisabled={isDisabled}
        isOwner={isOwner}
        onChat={handleChat}
        onLike={handleLike}
        onShare={handleShare}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      </div>

      {/* 상품 수정 모달 */}
      {item && (
        <EditProductModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          productId={itemId}
          onSuccess={() => {
            setShowEditModal(false);
            loadItemDetail(); // 상품 정보 새로고침
          }}
        />
      )}
    </>
  );
}

